/**
 * Unit conversion registry for lab test results.
 *
 * The single JavaScript implementation: the frontend imports this file directly
 * (src/util/GenericUtil.js, src/util/labMerge.js) rather than keeping a copy.
 * src/util/UnitConversion.js used to be that copy and had drifted.
 *
 * SYNC: unit_conversion.py is a Python port for the Python lambdas. Both are
 * pinned to tests/unit-cases.json, which both suites run — add a case there
 * rather than to one suite. Run the JS side with `npm run test:lambda`; the
 * main vitest config excludes lambda paths.
 */

/**
 * Spellings that denote the same magnitude, so a value carries across unchanged.
 *
 * Labs pick one arbitrarily: Quest reports TSH as mIU/L while fxRange.json
 * declares it in µIU/mL. normalizeUnit is purely textual, so those compared as
 * different, no marker-keyed conversion existed, and convertFxRange returned
 * null — dropping the functional band silently rather than showing a wrong one.
 *
 * Every pair here is exactly 1:1. Anything needing a factor belongs in
 * CONVERSION_REGISTRY instead, where it is keyed by marker.
 */
const UNIT_SYNONYMS = {
  'miu/l': 'uiu/ml',      // µIU/mL = 1e-6 IU/mL = 1e-3 IU/L
  'ug/l': 'ng/ml',        // 1e-6 g/L = 1e-9 g/mL
  'ng/l': 'pg/ml',
  'ug/ml': 'mg/l',
  'iu/l': 'u/l',          // enzyme activity: reported interchangeably
  'x10e9/l': 'x10e3/ul',  // 1e9/L = 1e3/µL
  'x10e12/l': 'x10e6/ul',
  'ml/min/1.73': 'ml/min/1.73m2',   // eGFR: the m2 is routinely truncated
  'ml/min/1.73sqm': 'ml/min/1.73m2',
};

function normalizeUnit(unit) {
  if (!unit) return '';
  let s = unit.toLowerCase()
    .replace(/\s+/g, '')
    .replace(/µ/g, 'u')
    .replace(/μ/g, 'u')
    .replace(/³/g, '3')
    .replace(/²/g, '2')
    .replace(/⁶/g, '6')
    .replace(/×/g, 'x');
  // Trailing annotations: "mg/dL (calc)", "% (calc)", "g/dL (%)". The note is
  // about how the value was derived, not what it is measured in.
  s = s.replace(/\((?:calc|calculated|%)\)$/, '');
  // "mcg" is how a lab writes µg when it cannot emit the symbol.
  s = s.replace(/^mcg/, 'ug');
  // Count prefixes. Quest spells them out in English and Spanish; LabCorp
  // abbreviates. All denote the same magnitude as x10e3 / x10e6 per µL.
  s = s.replace(/^(?:k|thousand|thous|thou|miles)\//, 'x10e3/');
  s = s.replace(/^(?:m|million|mill|millones)\//, 'x10e6/');
  // A count magnitude written with a separator instead of a superscript:
  // "10E3/uL" (LabCorp without the leading x), "10^3/uL", "10*3/uL" (UCUM, which
  // is what an HL7 lab feed carries) and "10x3/uL" (from the × sign above). Each
  // of these previously fell through unrecognised, and convertFxRange returns
  // null on an unmatched unit — so WBC and Platelets silently lost their
  // functional range. The denominator is required so the bare token "10x3",
  // which is not a unit, is still left alone. The exponent takes more than one
  // digit: RBC is reported as "10*12/L" and "10^12/L", and a single-digit capture
  // left exactly those unmatched while UNIT_SYNONYMS already held x10e12/l.
  s = s.replace(/^x?10[\^*xe](\d+)\//, 'x10e$1/');
  s = s.replace(/x10(\d)/, 'x10e$1');
  s = s.replace(/^10(\d)/, 'x10e$1');
  return UNIT_SYNONYMS[s] || s;
}

/**
 * Conversion factors, keyed by marker name (lowercased).
 *
 * Two name spaces reach this table and both must resolve. The lambdas normalize
 * at ingest and still see the lab's own wording ("LDL Chol Calc (NIH)"), while
 * the frontend looks up by the canonical alias from normalize-name-map.json
 * ("LDL"). The frontend and lambda copies of this file had each grown only the
 * half they needed, so a conversion that worked in one place silently returned
 * null in the other. Alias keys therefore point at one shared factor object
 * rather than repeating the numbers.
 *
 * Pairs that are exactly 1:1 do NOT belong here — UNIT_SYNONYMS handles those
 * for every marker at once, which is why ferritin and PSA no longer appear.
 */
const CHOLESTEROL_CONV = {
  'mg/dl|mmol/l': { factor: 0.02586 },
  'mmol/l|mg/dl': { factor: 38.67 },
};
const CBC_CONV = {
  'cells/ul|x10e3/ul': { factor: 0.001 },
  'x10e3/ul|cells/ul': { factor: 1000 },
};
const GRAMS_CONV = {
  'g/dl|g/l': { factor: 10 },
  'g/l|g/dl': { factor: 0.1 },
};
const BILIRUBIN_CONV = {
  'mg/dl|umol/l': { factor: 17.1 },
  'umol/l|mg/dl': { factor: 0.0585 },
};
const MONOVALENT_CONV = {
  'meq/l|mmol/l': { factor: 1 },
  'mmol/l|meq/l': { factor: 1 },
};
const THYROID_CONV = {
  'ng/dl|pg/ml': { factor: 10 },
  'pg/ml|ng/dl': { factor: 0.1 },
};
const CRP_CONV = {
  'mg/l|mg/dl': { factor: 0.1 },
  'mg/dl|mg/l': { factor: 10 },
};

const CONVERSION_REGISTRY = {
  'glucose': {
    'mg/dl|mmol/l': { factor: 0.0555 },
    'mmol/l|mg/dl': { factor: 18.018 },
  },
  'triglycerides': {
    'mg/dl|mmol/l': { factor: 0.01129 },
    'mmol/l|mg/dl': { factor: 88.57 },
  },

  // Lipids — canonical alias first, lab wording after.
  'cholesterol': CHOLESTEROL_CONV,
  'cholesterol, total': CHOLESTEROL_CONV,
  'hdl': CHOLESTEROL_CONV,
  'hdl cholesterol': CHOLESTEROL_CONV,
  'ldl': CHOLESTEROL_CONV,
  'ldl chol calc (nih)': CHOLESTEROL_CONV,
  'vldl': CHOLESTEROL_CONV,
  'non-hdl cholesterol': CHOLESTEROL_CONV,

  // CBC differentials — LabCorp abbreviates, and omits a space after "monocytes".
  'wbc': CBC_CONV,
  'platelets': CBC_CONV,
  'neutrophils (absolute)': CBC_CONV,
  'lymphocytes (absolute)': CBC_CONV,
  'lymphs (absolute)': CBC_CONV,
  'monocytes (absolute)': CBC_CONV,
  'monocytes(absolute)': CBC_CONV,
  'eosinophils (absolute)': CBC_CONV,
  'eos (absolute)': CBC_CONV,
  'basophils (absolute)': CBC_CONV,
  'baso (absolute)': CBC_CONV,
  'immature grans (absolute)': CBC_CONV,
  'immature grans (abs)': CBC_CONV,
  'rbc': {
    'cells/ul|x10e6/ul': { factor: 0.000001 },
    'x10e6/ul|cells/ul': { factor: 1000000 },
  },

  'hemoglobin': GRAMS_CONV,
  'albumin': GRAMS_CONV,

  'creatinine': {
    'mg/dl|umol/l': { factor: 88.42 },
    'umol/l|mg/dl': { factor: 0.01131 },
  },
  'bun': {
    'mg/dl|mmol/l': { factor: 0.357 },
    'mmol/l|mg/dl': { factor: 2.801 },
  },
  'bilirubin (total)': BILIRUBIN_CONV,
  'bilirubin, total': BILIRUBIN_CONV,
  'calcium': {
    'mg/dl|mmol/l': { factor: 0.2495 },
    'mmol/l|mg/dl': { factor: 4.008 },
  },
  'iron': {
    'ug/dl|umol/l': { factor: 0.1791 },
    'umol/l|ug/dl': { factor: 5.583 },
  },
  'uric acid': {
    'mg/dl|umol/l': { factor: 59.48 },
    'umol/l|mg/dl': { factor: 0.01681 },
  },
  'crp (high sensitivity)': CRP_CONV,
  'c-reactive protein, cardiac': CRP_CONV,

  'testosterone': {
    'ng/dl|nmol/l': { factor: 0.0347 },
    'nmol/l|ng/dl': { factor: 28.82 },
  },
  'estradiol': {
    'pg/ml|pmol/l': { factor: 3.671 },
    'pmol/l|pg/ml': { factor: 0.2724 },
  },
  'cortisol': {
    'ug/dl|nmol/l': { factor: 27.59 },
    'nmol/l|ug/dl': { factor: 0.03625 },
  },
  'vitamin d, 25-hydroxy': {
    'ng/ml|nmol/l': { factor: 2.496 },
    'nmol/l|ng/ml': { factor: 0.4006 },
  },
  'vitamin b12': {
    'pg/ml|pmol/l': { factor: 0.7378 },
    'pmol/l|pg/ml': { factor: 1.3554 },
  },
  'folate': {
    'ng/ml|nmol/l': { factor: 2.266 },
    'nmol/l|ng/ml': { factor: 0.4413 },
  },
  // mEq/L equals mmol/L only for monovalent ions. Calcium and magnesium are
  // divalent and differ by a factor of 2, which is why this is keyed by marker
  // rather than living in UNIT_SYNONYMS.
  'sodium': MONOVALENT_CONV,
  'potassium': MONOVALENT_CONV,
  'chloride': MONOVALENT_CONV,
  'bicarbonate': MONOVALENT_CONV,
  'co2': MONOVALENT_CONV,

  // Thyroid hormones: 1 ng/dL = 10 pg/mL. fxRange declares T3 in ng/dL and
  // Reverse T3 in pg/mL, while labs vary on both.
  't3': THYROID_CONV,
  'free t3': THYROID_CONV,
  't3, free': THYROID_CONV,
  'reverse t3': THYROID_CONV,
  't3 reverse': THYROID_CONV,
};

class UnitConverter {
  static convert(testName, value, fromUnit, toUnit) {
    if (value == null || !Number.isFinite(Number(value))) return null;
    const numValue = Number(value);

    const normFrom = normalizeUnit(fromUnit);
    const normTo = normalizeUnit(toUnit);
    if (normFrom === normTo) return numValue;

    const testKey = (testName || '').toLowerCase();
    const conversionMap = CONVERSION_REGISTRY[testKey];
    if (!conversionMap) return null;

    const convKey = `${normFrom}|${normTo}`;
    const conv = conversionMap[convKey];
    if (!conv) return null;

    return numValue * conv.factor + (conv.offset || 0);
  }

  static canConvert(testName, fromUnit, toUnit) {
    const normFrom = normalizeUnit(fromUnit);
    const normTo = normalizeUnit(toUnit);
    if (normFrom === normTo) return true;

    const testKey = (testName || '').toLowerCase();
    const conversionMap = CONVERSION_REGISTRY[testKey];
    if (!conversionMap) return false;

    return `${normFrom}|${normTo}` in conversionMap;
  }

  static normalizeToRange(result, oldLow, oldHigh, newLow, newHigh) {
    if (result == null || !Number.isFinite(Number(result))) return null;
    const r = Number(result);
    const oL = Number(oldLow);
    const oH = Number(oldHigh);
    const nL = Number(newLow);
    const nH = Number(newHigh);

    if (!Number.isFinite(oL) || !Number.isFinite(oH) ||
        !Number.isFinite(nL) || !Number.isFinite(nH)) return r;

    const oldSpan = oH - oL;
    if (oldSpan === 0) return r;

    return nL + ((r - oL) / oldSpan) * (nH - nL);
  }
}

export { UnitConverter, normalizeUnit, CONVERSION_REGISTRY };
